<script setup>
import { Handle, Position } from '@vue-flow/core';
import { NodeToolbar } from '@vue-flow/node-toolbar';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { triggerNodeAction } from '../nodeEventBus';
import { AUTOMATIONS } from 'dashboard/routes/dashboard/settings/automation/constants';
import useAutomationValues from 'dashboard/composables/useAutomationValues';

const props = defineProps({
  id: { type: String, default: '' },
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { getConditionDropdownValues } = useAutomationValues();
const isHovered = ref(false);
const isExpanded = ref(false);
let hoverTimeout = null;
const onMouseEnter = () => {
  clearTimeout(hoverTimeout);
  isHovered.value = true;
};
const onMouseLeave = () => {
  hoverTimeout = setTimeout(() => {
    isHovered.value = false;
  }, 300);
};

const matchLabel = computed(() => {
  const match = props.data?.condition_match || 'all';
  return t(`FLOW_BUILDER.NODE_EDITOR.CONDITION.${match.toUpperCase()}`);
});

const conditions = computed(() => props.data?.conditions || []);
const conditionsCount = computed(() => conditions.value.length);

const conditionSummary = computed(() => {
  if (!conditions.value.length) {
    return t('FLOW_BUILDER.NODE_EDITOR.CONDITION.NO_CONDITIONS');
  }
  return t('FLOW_BUILDER.NODE_EDITOR.CONDITION.CONDITIONS_COUNT', {
    count: conditionsCount.value,
  });
});

const attrNameMap = computed(() => {
  const map = {};
  Object.values(AUTOMATIONS).forEach(config => {
    config.conditions.forEach(c => {
      if (!map[c.key]) map[c.key] = c.name;
    });
  });
  return map;
});

const attrLabel = key => {
  const name = attrNameMap.value[key];
  if (!name) return key;
  const translated = t(`AUTOMATION.ATTRIBUTES.${name}`);
  return translated === `AUTOMATION.ATTRIBUTES.${name}` ? key : translated;
};

const operatorLabel = op => {
  const translated = t(`FILTER.OPERATOR_LABELS.${op}`);
  return translated === `FILTER.OPERATOR_LABELS.${op}` ? op : translated;
};

const valueLabel = (key, values) => {
  if (!values || !values.length) return '';
  const options = getConditionDropdownValues(key) || [];
  const resolved = values
    .map(v => {
      const found = options.find(o => o.id === v);
      return found ? found.name : v;
    })
    .join(', ');
  return resolved;
};

const visibleConditions = computed(() =>
  isExpanded.value ? conditions.value : conditions.value.slice(0, 2)
);

const toggleExpand = () => {
  isExpanded.value = !isExpanded.value;
};
</script>

<template>
  <div
    class="flow-node min-w-[150px] p-2.5 bg-white dark:bg-n-slate-1 rounded-lg text-[10px] shadow-md hover:shadow-lg border transition-all relative"
    :class="
      selected
        ? 'border border-green-500 shadow-lg'
        : 'border border-transparent hover:border-n-blue-8'
    "
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
  >
    <NodeToolbar :is-visible="isHovered" :position="Position.Top" :offset="8">
      <div
        class="flex gap-1 bg-white dark:bg-n-slate-2 rounded-lg shadow-lg p-1"
        @mouseenter="onMouseEnter"
        @mouseleave="onMouseLeave"
      >
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-blue-1 dark:hover:bg-n-blue-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DUPLICATE')"
          @click.stop="triggerNodeAction('duplicate', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#2563eb"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <rect width="14" height="14" x="8" y="8" rx="2" ry="2" />
            <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2" />
          </svg>
        </button>
        <button
          class="w-8 h-8 rounded-md flex items-center justify-center hover:bg-n-ruby-1 dark:hover:bg-n-ruby-2 transition-colors"
          :title="t('FLOW_BUILDER.SIDEBAR.DELETE')"
          @click.stop="triggerNodeAction('delete', props.id)"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="#dc2626"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M3 6h18" />
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" />
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" />
          </svg>
        </button>
      </div>
    </NodeToolbar>
    <Handle
      type="target"
      :position="Position.Left"
      class="!w-1.5 !h-1.5 !border !border-white"
      :title="t('FLOW_BUILDER.NODE_EDITOR.HANDLE_INPUT')"
    />
    <div class="flex items-center gap-2">
      <div
        class="w-7 h-7 rounded-lg flex items-center justify-center bg-n-amber-1 dark:bg-n-amber-2 shrink-0"
      >
        <span class="i-lucide-git-branch text-sm text-n-amber-11" />
      </div>
      <div class="flex flex-col">
        <span class="font-semibold text-n-slate-12 text-[9px]">
          {{ props.data?.label || t('FLOW_BUILDER.SIDEBAR.CONDITION') }}
        </span>
        <span class="text-[8px] text-n-slate-11">
          {{ matchLabel }}
        </span>
      </div>
    </div>
    <div class="mt-1.5 px-1 flex flex-col gap-0.5">
      <span class="text-[7px] text-n-slate-10 leading-tight">
        {{ conditionSummary }}
      </span>
      <template v-for="(cond, i) in visibleConditions" :key="i">
        <span
          class="text-[7px] text-n-slate-11 leading-tight truncate max-w-[130px]"
        >
          {{ attrLabel(cond.attribute_key) }}
          {{ operatorLabel(cond.filter_operator) }}
          {{ valueLabel(cond.attribute_key, cond.values) }}
        </span>
      </template>
      <button
        v-if="conditionsCount > 2"
        class="text-[7px] text-n-blue-9 font-medium leading-tight flex items-center gap-0.5 hover:text-n-blue-10 transition-colors"
        @click.stop="toggleExpand"
      >
        <span
          :class="isExpanded ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
          class="text-[8px]"
        />
        {{
          isExpanded
            ? t('FLOW_BUILDER.NODE_EDITOR.CONDITION.SHOW_LESS')
            : t('FLOW_BUILDER.NODE_EDITOR.CONDITION.SHOW_ALL')
        }}
      </button>
    </div>
    <div class="flex justify-between w-full mt-1.5 px-1">
      <span
        class="text-[5px] text-n-green-9 font-medium text-left flex-1 flex items-center gap-0.5"
      >
        <span class="i-lucide-check text-[6px]" />
        {{ t('FLOW_BUILDER.NODE_EDITOR.CONDITION.TRUE_OUTPUT') }}
      </span>
      <span
        class="text-[5px] text-n-ruby-9 font-medium text-right flex-1 flex items-center justify-end gap-0.5"
      >
        {{ t('FLOW_BUILDER.NODE_EDITOR.CONDITION.FALSE_OUTPUT') }}
        <span class="i-lucide-x text-[6px]" />
      </span>
    </div>
    <Handle
      id="true"
      type="source"
      :position="Position.Right"
      class="!bg-n-green-9 !w-1.5 !h-1.5 !border !border-white !top-[55%]"
      :title="t('FLOW_BUILDER.NODE_EDITOR.CONDITION.TRUE_OUTPUT')"
    />
    <Handle
      id="false"
      type="source"
      :position="Position.Right"
      class="!bg-n-ruby-9 !w-1.5 !h-1.5 !border !border-white !top-[85%]"
      :title="t('FLOW_BUILDER.NODE_EDITOR.CONDITION.FALSE_OUTPUT')"
    />
  </div>
</template>
